import {
  colors,
  ImpContainer,
  ImpFlex,
  ImpInlineLink,
  ImpSpacer,
  ImpText,
  spacing,
} from '@imprint-payments/imprint-ui';

import { EVENT } from '../../analytics/analyticsConsts';
import { useAppSelector, useImpState } from '../../app/hooks';
import { selectLanguage } from '../../app/languageSelectors';
import { selectMerchantData } from '../../app/merchantDataSelectors';
import { ANALYTICS } from '../../firebase/firebase';
import { getIssuerDisclosure } from '../../utils/disclosures';
import { LanguageCode } from '../../utils/language';
import {
  ESIGN_POLICY,
  merchantInfoByProductUUID,
  PRIVACY_NOTICE,
  type ProductUUID,
} from '../../utils/productUUIDConstants';
import footerLogoUrl from './FooterLogo.svg';

export default function DisclosureFooter() {
  const language: any = useAppSelector(selectLanguage);

  return (
    <ImpContainer
      borderTop={`1px solid ${colors.border.borderVariant}`}
      style={{ paddingTop: spacing.l }}
    >
      <ImpFlex direction="column" gap={spacing.s}>
        <DisclosureLinks />

        <CreditPullDisclosure />

        <IssuerStatement />

        {language.selected === LanguageCode.Spanish && (
          <ImpText typography="body3" color="#A7A6A4">
            {language.incomeHousingIDLanguageDisclosure}
          </ImpText>
        )}
      </ImpFlex>

      <ImpSpacer height={spacing.m} />

      <ImpFlex justify="flex-end">
        <FooterLogo />
      </ImpFlex>
    </ImpContainer>
  );
}

const FooterLogo = () => {
  return <img src={footerLogoUrl} alt="Imprint Logo" width={35} height={60} />;
};

const IssuerStatement = () => {
  const { language, merchantData } = useImpState();
  const productUUID = merchantData.productUUID as ProductUUID;
  const cardName = merchantData.rewardDetails.cardName;

  return (
    <ImpText color={colors.content.onSurfaceVariant} typography="body3">
      {getIssuerDisclosure(productUUID, cardName, language)}
    </ImpText>
  );
};

const CreditPullDisclosure = () => {
  const language: any = useAppSelector(selectLanguage);

  return (
    <ImpText
      color={colors.content.onSurfaceVariant}
      typography="body3"
      handleSuperscript
    >
      {language.creditPullDisclosure}
    </ImpText>
  );
};

const DisclosureLinks = () => {
  const { productUUID } = useAppSelector(selectMerchantData);
  const language = useAppSelector(selectLanguage);
  const languageCode = language.selected as LanguageCode;

  const cca_link =
    merchantInfoByProductUUID[productUUID].cardholderAgreement[languageCode];
  const terms =
    merchantInfoByProductUUID[productUUID].rewardsTerms[languageCode];
  const privacy_link = PRIVACY_NOTICE[languageCode];
  const esign_link = ESIGN_POLICY[languageCode];
  const cisn =
    merchantInfoByProductUUID[productUUID].infoSharingNotice[languageCode];

  return (
    <ImpText
      typography="body3"
      handleSuperscript
      key="income-housing-terms-text"
      color={colors.content.onSurfaceVariant}
    >
      <span>*</span>
      <ImpInlineLink
        underline
        url={cca_link}
        openInNewTab
        onClick={() =>
          ANALYTICS.logEvent(EVENT.INCOME_HOUSING_LINK_CLICKED, {
            link: 'terms_of_service',
          })
        }
      >
        {language.incomeHousingIDLink1}
      </ImpInlineLink>
      {language.disclosureLinksSeparator}
      <ImpInlineLink
        underline
        url={terms}
        openInNewTab
        onClick={() =>
          ANALYTICS.logEvent(EVENT.INCOME_HOUSING_LINK_CLICKED, {
            link: 'rewards_terms_and_conditions',
          })
        }
      >
        {language.incomeHousingIDLink5}
      </ImpInlineLink>
      {language.disclosureLinksSeparator}
      <ImpInlineLink
        underline
        url={privacy_link}
        openInNewTab
        onClick={() =>
          ANALYTICS.logEvent(EVENT.INCOME_HOUSING_LINK_CLICKED, {
            link: 'privacy_policy',
          })
        }
      >
        {language.incomeHousingIDLink2}
      </ImpInlineLink>
      {language.disclosureLinksSeparator}
      <ImpInlineLink
        underline
        url={esign_link}
        openInNewTab
        onClick={() =>
          ANALYTICS.logEvent(EVENT.INCOME_HOUSING_LINK_CLICKED, {
            link: 'e-sign_policy',
          })
        }
      >
        {language.incomeHousingIDLink3}
      </ImpInlineLink>
      {language.disclosureLinksAnd}
      <ImpInlineLink
        underline
        url={cisn}
        openInNewTab
        onClick={() =>
          ANALYTICS.logEvent(EVENT.INCOME_HOUSING_LINK_CLICKED, {
            link: 'cardholder_information_sharing_notice',
          })
        }
      >
        {language.incomeHousingIDLink4}
      </ImpInlineLink>
      .
    </ImpText>
  );
};
